import { useTranslation } from 'react-i18next';
import { ArrowDownToLine } from 'lucide-react';

import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRetryCryptoOnchainPayout } from '@/lib/api/crypto-onchain-platform';

import { PayoutRow } from './PayoutRow';
import { PAYOUT_STAGES, type PayoutStage, type PlatformPayout } from './types';

type Props = {
  payouts: PlatformPayout[];
  stage: PayoutStage | undefined;
  onStageChange: (stage: PayoutStage | undefined) => void;
  /** Superadmins may queue a manual sweep (it moves funds). */
  canRetry: boolean;
};

const ALL_STAGES = 'all';

/**
 * Every sweep, newest first, read live from crypto-api: what reached the merchant, the platform's share
 * (or why it was waived), and the error of a failed sweep. crypto-api retries failed sweeps on its own;
 * "Retry" queues one now, bypassing the auto-sweep switch and the dust threshold.
 */
export function PayoutsTable({ payouts, stage, onStageChange, canRetry }: Props) {
  const { t } = useTranslation('manageCryptoPayments');
  const retry = useRetryCryptoOnchainPayout();
  const retryingPayoutId = retry.isPending ? (retry.variables?.payoutId ?? null) : null;

  const handleRetry = (payoutId: string) => {
    if (retry.isPending) return;
    retry.mutate({ payoutId });
  };

  const handleStageChange = (value: string) => {
    const next = PAYOUT_STAGES.find((candidate) => candidate === value);
    onStageChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <Label htmlFor="crypto-payouts-stage" className="text-sm font-normal">
          {t('payouts.stageFilter')}
        </Label>
        <Select value={stage ?? ALL_STAGES} onValueChange={handleStageChange}>
          <SelectTrigger id="crypto-payouts-stage" className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STAGES}>{t('payouts.allStages')}</SelectItem>
            {PAYOUT_STAGES.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`payouts.stages.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {payouts.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ArrowDownToLine />
            </EmptyMedia>
            <EmptyTitle>{t('payouts.empty')}</EmptyTitle>
            <EmptyDescription>{t('payouts.emptyDescription')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('payouts.columns.store')}</TableHead>
              <TableHead>{t('payouts.columns.amount')}</TableHead>
              <TableHead>{t('payouts.columns.platformShare')}</TableHead>
              <TableHead>{t('payouts.columns.stage')}</TableHead>
              <TableHead>{t('payouts.columns.sweepTx')}</TableHead>
              <TableHead>{t('payouts.columns.createdAt')}</TableHead>
              <TableHead className="text-right">{t('payouts.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payouts.map((payout) => (
              <PayoutRow
                key={payout.id}
                payout={payout}
                canRetry={canRetry}
                isRetrying={retryingPayoutId === payout.id}
                onRetry={handleRetry}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
